<template>
  <div class="h-full">
    <div class="setting-wrapper overflow-hidden relative bg-zt-white">
      <div class="settings-content pt-4 w-full overflow-auto">
        <div class="py-4 px-8">
          <h2 class="mb-8 text-zt-xl">{{ $t('message.abm.folders.settingPanel.loadDocument') }}</h2>
          <input-file-upload
            ref="ztInputFileUpload"
            v-model="file" :label="$t('message.abm.folders.settingPanel.loadDocument')"
            :is-loading="pdfLoading"
            @input-file="selectFile"
            @input-file-clear="inputFileClear"
            data-vs-input="inputFile"
          />
          <div
            v-if="error" class="mt-4 text-zt-secondary-200 text-zt-lg"
            style="max-width: 24rem"
          >
            <p v-html="$sanitize(error)"></p>
          </div>
          <pdf-info v-if="documentLoaded && !error" :document="{ ...document, ...pdfInfo }" />
        </div>
        <div class="py-4 px-8" v-if="isActiveSplitCrop">
          <h2 class="mb-4 text-zt-xl">{{ $t('message.abm.folders.settingPanel.actions') }}</h2>
          <rotate-action-panel ref="ztExpansionPanelRotate" :disabled="!documentLoaded" @clear="clear" />
          <cut-pdf-action-panel focus-panel ref="ztExpansionPanelCut" :disabled="!documentLoaded" @clear="clear" />
        </div>
        <div class="py-4 px-8">
          <h2 class="mb-4 text-zt-xl">{{ $t('message.abm.folders.settingPanel.position') }}</h2>
          <employee-signature-action-panel
            ref="ztExpansionPanelEmployee"
            focus-panel
            :disabled="!documentLoaded"
            :document="document"
            :preset="employee"
            @clear="clear"
          />
          <employer-signature-action-panel
            ref="ztExpansionPanelEmployer"
            v-if="isActiveSplitCrop"
            focus-panel
            :disabled="!documentLoaded"
            :document="document"
            :preset="employer"
            @clear="clear"
          />
        </div>
      </div>
      <div class="config-footer w-full absolute bottom-0 left-0 rounded-b">
        <div class="flex justify-center items-center h-24 px-4">
          <zt-button text color="dark-300" class="button-cancel mr-4" @click="closeDialog" data-vs-button="closeDialog">
            {{ $t('message.abm.folders.settingPanel.exit') }}
          </zt-button>
          <zt-button class="w-30" :disabled="!fileLoaded" @click="activeModalConfirm(true)" data-vs-button="activeModalConfirm">
            {{ $t('message.abm.folders.settingPanel.ok') }}
          </zt-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { isEmpty, isEqual, remove } from 'lodash'
import ExpansionPanel from './generic/ExpansionPanel'
import InputFileUpload from './generic/InputFileUpload'
import RotateActionPanel from './panels/RotatePdfActionPanel'
import CutPdfActionPanel from './panels/CutPdfActionPanel'
import EmployerSignatureActionPanel from './panels/EmployerSignatureActionPanel'
import EmployeeSignatureActionPanel from './panels/EmployeeSignatureActionPanel'
import PdfInfo from './PDFInfo'
import Signature from '@/apiClient/Signature'
import Folders from '@/apiClient/Folders'
import tools from '../../../utils/tools'
export default {
  name: 'SignatureSettingPanel',
  components: {
    EmployeeSignatureActionPanel,
    EmployerSignatureActionPanel,
    ExpansionPanel,
    CutPdfActionPanel,
    InputFileUpload,
    PdfInfo,
    RotateActionPanel
  },
  provide() {
    return {
      setFocusPanel: this.setFocusPanel,
      setPageTo: this.setPageTo,
      signatureState: this.signatureState,
      signatureToolApplied: this.signatureToolApplied,
      wrapperPanels: this,
    }
  },
  inject: [
    'activeErrorDialog',
    'activeModalConfirm',
    'closeDialog',
    'modalState',
    'resetModalState',
    'setModalState',
    'setModalStateProperty',
    'updateVisorPDF',
    'updateToolPanel'
  ],
  props: {
    active: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      appliedConfig: [],
      children: [],
      document: {},
      documentLoaded: false,
      employer: {},
      employee: {},
      error: "",
      file: '',
      focused: '',
      page: 1,
      pdfInfo: {},
      pdfLoading: false,
      presets: null
    }
  },
  computed: {
    ...mapGetters(['isActionAllowed']),
    isActiveSplitCrop() {
      return this.isActionAllowed(105)
    },
    fileLoaded() {
      return this.file !== ''
    }
  },
  created() {
    this.folders = new Folders()
    this.signatureApi = new Signature()
    this.$on("create-children", (newChild) => {
      this.children.push(newChild)
    })
  },
  methods: {
    async selectFile(value) {
      this.error = ''
      this.pdfLoading = true
      try {
        const pdfInfo = await this.folders.getPDFInfo(value)
        if (!pdfInfo.data.data.supported) {
          this.handlerError("NO_SUPPORTED")
          this.pdfLoading = false
          return
        }
        this.file = value
        this.pdfInfo = pdfInfo.data.data
        this.pdfLoading = false
        const blob = new Blob([value], { type: 'application/pdf' })
        const reader = new FileReader()
        reader.onloadend = () => {
          const { paper, orientation } = this.pdfInfo
          this.setModalStateProperty({ paper, orientation }, 'employee', 'preset')
          this.setModalStateProperty({ paper, orientation }, 'employer', 'preset')
          this.updateVisorPDF({
            base64: reader.result,
            file: value,
            paper,
            page: 1
          })
        }
        reader.readAsDataURL(blob)
      } catch (e) {
        this.pdfLoading = false
        this.handlerError(e.response.data.codes[0].code)
      }
    },
    _updateInfo() {
      if (isEqual(this.presets, this.modalState.config)) return
      const { PDFJSObject, cutPdf, file, employee, employer, rotation } = this.modalState.config
      if (!isEmpty(PDFJSObject)) {
        this.document = PDFJSObject
        this.documentLoaded = true
        this.employee = employee.preset
        this.employer = employer.preset
        this.file = file || ''
        this.page = PDFJSObject.page.pdfPage.pageNum
        if (!isEmpty(this.$refs)) {
          this.$refs.ztExpansionPanelEmployee.$data.shown = employee.preset.show
          if (this.isActiveSplitCrop) {
            this.$refs.ztExpansionPanelCut.applied = cutPdf.preset.applied
            this.$refs.ztExpansionPanelRotate.applied = rotation > 0
            this.$refs.ztExpansionPanelEmployer.$data.shown = employer.preset.show
          }
        }
      }
    },
    clear(name) {
      switch (name) {
        case 'cutPdf':
        case 'rotation':
          this.inputFileClear()
          this.setModalState({
            cutPdf: {
              isFocused: false,
              isVisible: false,
              isChanged: true,
              preset: {
                applied: false,
                coordinates: {},
                points: [],
                show: false,
              }
            },
            rotation: 0,
            rotationPending: false,
            rotationApplied: false,
          })
          break
        case 'employee':
          this.$refs.ztExpansionPanelEmployee.shownSignatureEmployee(false)
          this.$refs.ztExpansionPanelEmployee.$data.shown = false
          this.employee = this.modalState.config.employee
          break
        case 'employer':
          this.$refs.ztExpansionPanelEmployer.shownSignatureEmployer(false)
          this.$refs.ztExpansionPanelEmployer.$data.shown = false
          this.employer = this.modalState.config.employer
          break
      }
    },
    handlerError(code) {
      this.error = this.$t(`message.abm.folders.errors.${code}`)
    },
    inputFileClear() {
      this.file = ''
      this.error = ''
      this.documentLoaded = false
      this.page = 1
      this.updateVisorPDF({
        base64: '',
        cutPdf: {
          isFocused: false,
          isVisible: false,
          isChanged: true,
          preset: {
            applied: false,
            coordinates: {},
            points: [],
            show: false,
          }
        },
        file: '',
        page: 1,
        PDFJSObject: null,
        rotation: 0,
        rotationPending: false,
        rotationApplied: false,
      })
      for(const child of this.children) if (child.hasOwnProperty('close')) child.close()
    },
    setFocusPanel(e) {
      this.focused = e.$attrs.name || ''
      for (const { $attrs: { name }, _props } of this.children) {
        const isFocused = e.$attrs.name === name && this.focused.length >  0
        if (_props.focusActive === true) {
          this.setModalStateProperty({ isFocused  }, name)
          if (isFocused) this.setFocusPage(e.$attrs.name)
        }
      }
    },
    setFocusPage(name) {
      const { preset: { page } } = this.modalState.config[name]
      if (!page) return
      this.updateVisorPDF({ page })
    },
    signatureState(signatureKeyName, shown) {
      const {
        [signatureKeyName]: signature,
        orientation,
        paper
      } = this.modalState.config
      return !shown ? {
        page: 1,
        paper,
        position: 'CB',
        orientation,
        show: false,
        x: -160,
        y: -160,
        ...(signatureKeyName === 'employee' && { default: false })
      } :
      {
        ...signature.preset,
        ...{
          orientation,
          paper,
          position: 'LT',
          show: true,
        },
        ...(signatureKeyName === 'employee' && { default: false })
      }
    },
    signatureToolApplied(add, id) {
      remove(this.appliedConfig, { id })
      if (add) this.appliedConfig.push(tools[id])
      this.setModalState({ appliedConfig: this.appliedConfig })
    },
    showFileDialog() {
      this.$refs.ztInputFileUpload.$refs.inputFile.click()
    }
  },
};
</script>

<style scoped lang="scss">
.settings-content {
  overflow-y: auto;
  max-height: calc(100% - 6rem);
}
.settings-content::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.settings-content::-webkit-scrollbar {
  width: 0.625rem;
}
.settings-content::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #8c8c8c;
}
h2 {
  color: #333333;
}
.bg-viewer {
  background: #717171;
}
.setting-wrapper {
  height: calc(100% - 1.25rem);
  border-radius: 5px;
}
.config-footer {
  background-color: #eeeeee;
}
.input-number-wrapper {
  width: 33px;
  input {
    border-color: #4f41a3 !important;
  }
}
.font-125 {
  font-size: 1.125rem;
}
::v-deep {
  .setting-button {
    width: 100% !important;
    justify-content: flex-start !important;
    font-size: 1.125rem !important;
    height: 50px !important;

    .zt-button-text {
      margin: 0 !important;
    }

    .zt-icon {
      position: absolute;
      right: 15px;
      top: 18px;
    }
  }
  .button-cancel {
    color: #707070 !important;
    background: transparent !important;
    padding: 0 !important;
    font-size: 1.125rem !important;

    .zt-button-text {
      margin: 0 !important;
    }
  }
  .zt-modal {
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
      0 1px 8px rgba(0, 0, 0, 0.2);
  }
  .zt-modal.rounded {
    border-radius: 0.375rem !important;
  }
  button.button-cancel {
    color: #707070 !important;
    background-color: transparent !important;
  }
  .zt-modal .zt-modal-body {
    height: calc(100% - 50px);
  }
  .rotate-button {
    font-size: 14px;
    color: #4f41a3;
    border-color: #4f41a3 !important;
  }
}
</style>
